import type {ReactElement} from 'react';
import {useState} from 'react';
import React from 'react';
import type {NotificationsPanelProps} from './notifications-panel.types';
import {NotificationsPanelDisplayType} from './notifications-panel.types';
import styles from './notifications-panel.module.scss';
import type {PanelFooterProps} from '@Components/panel';
import {Panel, PanelFooter, PanelFooterPrimaryDefaultAction} from '@Components/panel';
import {useAppDispatch} from '@/hooks';
import {setPanelDisplayType} from '@Panels/notifications-panel/notifications-panel-slice';
import {areAllNotificationsRead} from '@Libraries/notifications-panel-library';
import {NotificationsPanelHeader} from '@Panels/notifications-panel/components/notifications-panel-header/notifications-panel-header';
import {NotificationsPanelContent} from '@Panels/notifications-panel/components/notifications-panel-content/notifications-panel-content';
import {Size, Type} from '@Components/button';
import type {PanelFooterPrimaryActionType} from '@Components/panel/components/panel-footer-primary-actions';
import type {PanelFooterSecondaryActionType} from '@Components/panel/components/panel-footer-secondary-actions';
import {
  useAllNotifications,
  useMarkAllNewNotificationsAsSeen,
  useMarkAllNotificationsAsRead,
  useNotificationPanelState,
} from '@Panels/notifications-panel/notifications-panel-hooks';
import {GA4EventName} from '@Libraries/ga-events';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import {NOTIFICATIONS_PANEL_ID} from '@Modals/notifications-modal/notifications-modal-library';
import {closePanel} from '@Components/panel/panel-reducer';

export function NotificationsPanel(props: NotificationsPanelProps): ReactElement {
  const dispatch = useAppDispatch();
  const panelState = useNotificationPanelState();
  const [hasShadow, setHasShadow] = useState(false);
  const allNotifications = useAllNotifications();
  const markAllNotificationsAsRead = useMarkAllNotificationsAsRead();
  const markAllNewNotificationsAsSeen = useMarkAllNewNotificationsAsSeen();

  const customCloseFunction = (): void => {
    if (props.isMobile) {
      markAllNewNotificationsAsSeen(allNotifications);
      if (allNotifications.length > 0) {
        dispatch(setPanelDisplayType(NotificationsPanelDisplayType.LOADED));
      } else {
        dispatch(setPanelDisplayType(NotificationsPanelDisplayType.EMPTY));
      }
    }
    dispatch(closePanel(NOTIFICATIONS_PANEL_ID));
    if (props.onClosePanel) {
      props.onClosePanel();
    }
  };

  const customClose = useCustomPanelClose(NOTIFICATIONS_PANEL_ID, customCloseFunction);

  const getPanelPrimaryActions = (): PanelFooterPrimaryActionType[] => {
    return [
      <PanelFooterPrimaryDefaultAction
        onClick={() => {
          window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_MARK_READ);
          markAllNotificationsAsRead(allNotifications);
        }}
        size={Size.SMALL}
        type={Type.GHOST}
        text={window.i18next.t('pmwjs_notifications_mark_all_read')}
        disabled={areAllNotificationsRead(allNotifications)}
      />,
    ];
  };
  const getPanelSecondaryActions = (): PanelFooterSecondaryActionType[] | undefined => {
    return [
      <PanelFooterPrimaryDefaultAction
        onClick={() => {
          dispatch(setPanelDisplayType(NotificationsPanelDisplayType.SETTINGS));
          window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_SETTINGS_CLICK);
        }}
        size={Size.SMALL}
        icon={'icon-settings'}
        type={Type.SECONDARY}
        text={window.i18next.t('pmwjs_settings')}
      />,
    ];
  };

  const getPanelFooter = (): undefined | ReactElement<PanelFooterProps> => {
    if (panelState === NotificationsPanelDisplayType.LOADED) {
      return (
        <PanelFooter
          className={`shadow-static-elevation-2 ${styles.zIndexPlacement}`}
          actionsContainerClassName={'spacing-p-4'}
          primaryActions={getPanelPrimaryActions()}
          secondaryActions={getPanelSecondaryActions()}
        ></PanelFooter>
      );
    }
    return undefined;
  };

  return (
    <Panel
      panelFooter={props.isMobile ? getPanelFooter() : undefined}
      className={`${props.className} ${styles.panelStyles}`}
      panelHeader={<NotificationsPanelHeader isMobile={props.isMobile} hasShadow={hasShadow} customClose={customClose} />}
      panelContent={<NotificationsPanelContent isMobile={props.isMobile} setHasShadow={setHasShadow} customClose={customClose} />}
    />
  );
}
